import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Clock, ArrowLeft } from "lucide-react";
import { fetchArticle, resolveMediaUrl } from "@/lib/api";
import { BLOG } from "@/constants/testIds";
import SEO from "@/components/SEO";
import { articleLd, breadcrumbLd } from "@/lib/jsonLd";

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchArticle(slug)
      .then((d) => setArticle(d))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-32">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#ff9c00] border-t-transparent rounded-full animate-spin"/>
          <p className="overline">Generando contenido con IA...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen pt-32 max-w-3xl mx-auto px-6">
        <p className="overline">404</p>
        <h1 className="font-heading text-4xl mt-4 ink-heading">Artículo no encontrado</h1>
        <Link to="/blog-dermo-facial" className="text-[#3D1F0A] mt-8 inline-flex items-center gap-2 link-underline font-semibold">
          <ArrowLeft size={16}/> Volver al blog
        </Link>
      </div>
    );
  }

  return (
    <article className="py-24" data-testid={BLOG.article}>
      <SEO
        title={`${article.title} | Blog Dermo Facial Asirox`}
        description={article.excerpt?.slice(0, 220)}
        keywords={`${article.tags?.join(", ").toLowerCase() || ""}, equipos dermo faciales, aparatología estética, Asirox`}
        ogImage={resolveMediaUrl(article.hero_image)}
        jsonLd={[
          articleLd(article),
          breadcrumbLd([
            { name: "Inicio", url: "/" },
            { name: "Blog", url: "/blog-dermo-facial" },
            { name: article.title, url: `/blog-dermo-facial/${article.slug}` },
          ]),
        ]}
      />
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Link to="/blog-dermo-facial" className="text-black/60 inline-flex items-center gap-2 hover:text-[#3D1F0A] link-underline text-sm">
          <ArrowLeft size={14}/> Volver al blog
        </Link>

        <p className="overline mt-8">{article.category}</p>
        <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter mt-4 leading-tight ink-heading">{article.title}</h1>

        <div className="flex items-center gap-4 mt-6 text-xs text-black/60 font-mono">
          <span className="flex items-center gap-2"><Clock size={12}/> {article.read_time}</span>
          {article.tags?.length ? <span>· {article.tags.slice(0, 3).join(" · ")}</span> : null}
        </div>

        <div className="relative aspect-[16/9] mt-10 overflow-hidden border border-black/15 bg-black">
          <img src={resolveMediaUrl(article.hero_image)} alt={article.title} className="absolute inset-0 w-full h-full object-cover opacity-95"/>
        </div>

        <p className="text-black text-lg leading-relaxed mt-10 font-medium">{article.excerpt}</p>

        <div
          className="prose-asx mt-12"
          dangerouslySetInnerHTML={{ __html: article.content || "<p>Contenido próximamente.</p>" }}
        />

        <div className="mt-20 border border-[#ff9c00]/40 bg-[#f1e3cb] p-8">
          <p className="overline">¿Listo para el siguiente paso?</p>
          <h3 className="font-heading text-2xl mt-3 ink-heading font-bold">Recibe un análisis personalizado para tu centro</h3>
          <p className="text-black/80 mt-3">Nuestro equipo te enviará una propuesta técnica y económica adaptada a tu modelo de negocio.</p>
          <Link to="/contacto?intent=quote" className="btn-primary mt-6 inline-flex">Solicitar análisis</Link>
        </div>
      </div>
    </article>
  );
}
